// Formulario "Crear empresa en el holding" (ficha del holding → Empresas): estado inicial,
// validación, cuerpo de POST contracts/holdings/:id/child-companies y texto de sus errores.
// Puro a propósito: el modal solo pinta y estos helpers se prueban sin DOM.
import { isEmail, isValidTaxId } from "../../settings/sections/invoicing/fiscalValidation";

export const WEEK_DAYS = [
  { value: "Monday", label: "Lun" },
  { value: "Tuesday", label: "Mar" },
  { value: "Wednesday", label: "Mié" },
  { value: "Thursday", label: "Jue" },
  { value: "Friday", label: "Vie" },
  { value: "Saturday", label: "Sáb" },
  { value: "Sunday", label: "Dom" },
];

const DEFAULT_WORKING_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

/** Integraciones que contracts acepta al crear (lista cerrada, solo `sender_id`). */
export const SENDER_INTEGRATIONS = ["Sendu"];

export const ADDRESS_TYPES = ["BODEGA", "OFICINA"];

const PICKUP_TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

export const emptyAddress = () => ({
  address: "",
  type: "BODEGA",
  state: "",
  municipality: "",
  contactPhone: "",
});

const emptyContact = () => ({ name: "", email: "", phone: "", role: "", rut: "" });

/** Dirección fiscal heredada del holding (calle, comuna, región), editable. */
export function holdingFiscalAddressText(holding) {
  const fa = holding?.fiscalAddress ?? {};
  return [fa.street, fa.municipality, fa.state].filter(Boolean).join(", ");
}

export function initialChildForm(holding) {
  return {
    name: "",
    legalName: "",
    fiscalAddress: holdingFiscalAddressText(holding),
    ownTaxId: false,
    businessIdentifier: "",
    pickUpTime: "",
    workingDays: [...DEFAULT_WORKING_DAYS],
    shippingAddresses: [emptyAddress()],
    primary: emptyContact(),
    withBilling: false,
    billing: emptyContact(),
    withIntegration: false,
    integrationName: SENDER_INTEGRATIONS[0],
    senderId: "",
  };
}

const blank = (value) => !String(value ?? "").trim();

/**
 * Errores por campo (clave → texto sin traducir; el modal lo pasa por `t`). Vacío = válido.
 * `countryCode` ('CL' | 'AR') decide la regla del RUT/CUIT propio.
 */
export function validateChildForm(form, countryCode) {
  const errors = {};
  if (blank(form.name)) errors.name = "El nombre es obligatorio.";
  if (blank(form.legalName)) errors.legalName = "La razón social es obligatoria.";
  if (blank(form.fiscalAddress)) errors.fiscalAddress = "La dirección fiscal es obligatoria.";
  if (form.ownTaxId && !isValidTaxId(countryCode, form.businessIdentifier)) {
    errors.businessIdentifier =
      countryCode === "AR" ? "El CUIT no es válido." : "El RUT no es válido.";
  }
  if (!PICKUP_TIME.test(form.pickUpTime ?? "")) {
    errors.pickUpTime = "Indica la hora de retiro (HH:mm).";
  }
  if (!form.workingDays?.length) errors.workingDays = "Elige al menos un día.";

  form.shippingAddresses.forEach((a, i) => {
    if (blank(a.address)) errors[`address_${i}`] = "La dirección es obligatoria.";
    if (blank(a.state)) errors[`state_${i}`] = "La región es obligatoria.";
    if (blank(a.municipality)) errors[`municipality_${i}`] = "La comuna es obligatoria.";
    if (blank(a.contactPhone)) errors[`contactPhone_${i}`] = "El teléfono es obligatorio.";
  });

  const contactErrors = (contact, prefix) => {
    if (blank(contact.name)) errors[`${prefix}Name`] = "El nombre es obligatorio.";
    if (!isEmail(contact.email)) errors[`${prefix}Email`] = "El correo no es válido.";
    if (blank(contact.phone)) errors[`${prefix}Phone`] = "El teléfono es obligatorio.";
  };
  contactErrors(form.primary, "primary");
  if (form.withBilling) {
    contactErrors(form.billing, "billing");
    if (!blank(form.billing.rut) && !isValidTaxId(countryCode, form.billing.rut)) {
      errors.billingRut = "El RUT no es válido.";
    }
  }

  if (form.withIntegration && blank(form.senderId)) {
    errors.senderId = "Indica el sender_id de la integración.";
  }
  return errors;
}

const trimmed = (value) => String(value ?? "").trim();

function contactBody(contact, { withRut }) {
  return {
    name: trimmed(contact.name),
    email: trimmed(contact.email).toLowerCase(),
    phone: trimmed(contact.phone),
    ...(trimmed(contact.role) ? { role: trimmed(contact.role) } : {}),
    ...(withRut && trimmed(contact.rut) ? { rut: trimmed(contact.rut) } : {}),
  };
}

/**
 * Cuerpo del POST. Solo los campos del DTO de contracts (whitelist estricta: cualquier otro
 * es 400). El país sale del operador; sin RUT propio, contracts asigna el correlativo.
 */
export function buildChildBody(form, operationsCountry) {
  return {
    name: trimmed(form.name),
    legalName: trimmed(form.legalName),
    fiscalAddress: trimmed(form.fiscalAddress),
    ...(form.ownTaxId ? { businessIdentifier: trimmed(form.businessIdentifier) } : {}),
    ...(operationsCountry ? { operationsCountry } : {}),
    pickUpTime: form.pickUpTime,
    workingDays: form.workingDays,
    shippingAddresses: form.shippingAddresses.map((a) => ({
      address: trimmed(a.address),
      type: a.type,
      state: trimmed(a.state),
      municipality: trimmed(a.municipality),
      contactPhone: trimmed(a.contactPhone),
    })),
    contacts: {
      primary: contactBody(form.primary, { withRut: false }),
      ...(form.withBilling ? { billing: contactBody(form.billing, { withRut: true }) } : {}),
    },
    ...(form.withIntegration
      ? {
          integration: {
            integrationName: form.integrationName,
            sender_id: trimmed(form.senderId),
          },
        }
      : {}),
  };
}

/** "CHILE" / "ARGENTINA" (lo que guarda `company.operationsCountry`), o null. */
export function operationsCountryOf(countryCode) {
  if (countryCode === "CL") return "CHILE";
  if (countryCode === "AR") return "ARGENTINA";
  return null;
}

const ERROR_TEXTS = {
  template_contract_missing:
    "Ninguna empresa del holding tiene un contrato activo y vigente con tu operador para replicar. Crea la primera empresa desde «Nuevo contrato» (empresa nueva) y vincúlala al holding.",
  holding_without_operator: "El holding no tiene operador logístico. Pídele a soporte de Beartrack que lo asigne.",
  holding_without_tax_id: "El holding no tiene RUT para generar el correlativo: usa un RUT propio.",
  sender_id_taken: "Ese sender_id ya está asociado a otra empresa.",
  tax_id_taken: "Ese RUT ya está registrado. Si usaste el correlativo, vuelve a intentarlo.",
  integration_not_allowed: "Esa integración no se puede cargar al crear la empresa.",
  invalid_tax_id: "El RUT no es válido.",
  operations_country_required: "No se pudo determinar el país de la empresa.",
  holding_not_found: "El holding no existe o no es de tu operador.",
  forbidden_role:
    "Solo un administrador o un superadministrador logístico puede crear empresas en un holding.",
};

/** Texto (sin traducir) de un error del POST; `err` es el error de axios. */
export function childErrorText(err) {
  const code = err?.response?.data?.code;
  if (code && ERROR_TEXTS[code]) return ERROR_TEXTS[code];
  if (err?.response?.status === 400) return "Revisa los datos: hay campos que no son válidos.";
  return "No se pudo crear la empresa. Intenta nuevamente.";
}
